import { isNullish } from '@sas/nova-core/typeguards';
import { getTextLocalizer } from '../../locale';
import { addBundle } from '../../locale/createUseTextLocalizer';
import { parseAndFormatInput, validateNumericInput } from '../Input';
import bundle from './NovaComponents-NumericStepper-gui-icu.properties';
addBundle(bundle);
let localize;
getTextLocalizer('NovaComponents-NumericStepper-gui-icu').then(localizer => {
  localize = localizer;
});

// this function convert a number from scientific notation, if it is, to decimal floating-point literal.
// for example, a number with scientific notation is '1.234e-15', its decimal floating-point literal is '0.000000000000001234'
export function getDecimalFloatPointLiteral(scientificNotation) {
  const scientificNotationRegex = /^([+-])?(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/;
  const match = scientificNotation.toString().match(scientificNotationRegex);
  /* match array elements for value '-1.012345678901234567890123456789e-10'
    0 ='-1.012345678901234567890123456789e-10'
    1 ='-'
    2 ='1'
    3 ='012345678901234567890123456789'
    4 ='-10'
  */

  if (!match) {
    return scientificNotation.toString();
  }
  const [, sign = '', dotLeft = '', dotRight = '', e = '0'] = match;
  const exponent = parseInt(e, 10);
  const [leftPart, rightPart] = exponent > 0 ? [dotLeft, dotRight.padEnd(exponent, '0')] : [dotLeft.padStart(1 - exponent, '0'), dotRight];
  const dotPosition = leftPart.length + exponent;
  const whole = leftPart + rightPart;
  const resultStr = normalizeNumberString(`${whole.slice(0, dotPosition)}.${whole.slice(dotPosition)}`);
  return `${sign}${resultStr}`;
}
export function doAccuratePlus(a, b) {
  const [[bigIntA, bigIntB], intLength, decLength] = convertToBigInt(a, b);
  const sum = bigIntA + bigIntB;
  const [sign, result] = sum < 0 ? ['-', -sum] : ['', sum];
  let resultStr = result.toString().padStart(intLength + decLength, '0');
  const dotPosition = resultStr.length - decLength;
  resultStr = normalizeNumberString(`${resultStr.slice(0, dotPosition)}.${resultStr.slice(dotPosition)}`);
  return `${sign}${resultStr}`;
}

// normalize a number string
// trimming leading and trailing zeros,
// adding leading zero if it is a decimal number,
// and replacing empty string with '0'.
export function normalizeNumberString(value) {
  return value.replace(/^0+|(?<=[.]\d+)(0+$)|([.]0*$)/g, '').replace(/^[.]/, '0.').replace(/^$/, '0');
}
export function convertToBigInt(...numbers) {
  const standardNumbers = numbers.map(n => getDecimalFloatPointLiteral(n));
  const parts = standardNumbers.map(s => {
    const [intPart = '', decPart = ''] = s.toString().split('.');
    return [intPart, decPart];
  });
  const [maxIntLength, maxDecLength] = parts.reduce(([maxIntLength, maxDecLength], [intPart, decPart]) => [Math.max(maxIntLength, intPart.length), Math.max(maxDecLength, decPart.length)], [0, 0]);
  const bigInts = parts.map(part => BigInt(part[0] + part[1].padEnd(maxDecLength, '0')));
  return [bigInts, maxIntLength, maxDecLength];
}
export function compare(a, b) {
  const [[bigIntA, bigIntB]] = convertToBigInt(a, b);
  return bigIntA - bigIntB;
}
export function isLarger(a, b) {
  return compare(a, b) > 0;
}
export function isLess(a, b) {
  return compare(a, b) < 0;
}
export function isEqual(a, b) {
  return isNotANumber(a) || isNotANumber(b) ? a === b : compare(a, b) === BigInt(0);
}
export function isInteger(value) {
  const standardNotation = normalizeNumberString(getDecimalFloatPointLiteral(value));
  return !standardNotation.includes('.');
}

/**
 * Validates whether a string is a valid number.
 * @param inputValue - The user input string.
 * @returns Whether it is a valid number.
 */
export const isValidNumericLiteral = inputValue => {
  // Regular expression: supports integers, decimals, positive/negative signs, and scientific notation
  const numberRegex = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
  return numberRegex.test(inputValue);
};
export function round(value, fractionDigits) {
  if (isNotANumber(value)) return NaN.toString();
  const roundingValue = `${value.toString().startsWith('-') ? '-' : ''}5e${-(fractionDigits + 1)}`;
  const result = doAccuratePlus(value, roundingValue);
  const [intPart, decPart = ''] = result.split('.');
  return normalizeNumberString(`${intPart}.${decPart.slice(0, fractionDigits)}`);
}

// support high precision number which can't be presented by Number.

export function isNotANumber(value) {
  return typeof value === 'number' ? isNaN(value) : isNullish(value) ? true : !isValidNumericLiteral(value);
}
/**
 * This is a range validator of for setting min and max values.
 * This is an enhanced version of function validateRangeInput in useRangeValidationAdapter.ts, supporting high-precision numbers.
 * @param props  of type ValidateRangeNumericStepperProps
 * @returns ValidateNumericInputResultType
 *
 */
export const validateRangeInputE = props => {
  let isError = false,
    status = undefined,
    message = undefined;
  const incMode = props.inclusiveMode >= 0 && props.inclusiveMode <= 3 ? props.inclusiveMode : 3;

  // to validate numeric value
  const result = validateNumericInput(props.value, props.appLocale, props.isRequired, props.allowIntegersOnly, props.isLive, props.minLength, props.numberFormatOptions, props.parseAndFormatFunction, props.formatter);
  const parsedValues = parseAndFormatInput(props.value, props.appLocale, props.numberFormatOptions, undefined, props.isLive);
  if (result.isError) {
    isError = result.isError;
    status = result.status;
    message = result.message;
  } else {
    if (props.value) {
      const {
        strRawValue: parsedMinValue,
        formatValue: minValueLocaleString
      } = parseAndFormatInput(props.minValue.toString(), undefined, props.numberFormatOptions, undefined, props.isLive);
      const {
        strRawValue: parsedMaxValue,
        formatValue: maxValueLocaleString
      } = parseAndFormatInput(props.maxValue.toString(), undefined, props.numberFormatOptions, undefined, props.isLive);
      if (isLarger(parsedValues.strRawValue, parsedMaxValue)) {
        if (incMode < 2) {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.maxRangeExclusive.msg', {
            maxValue: maxValueLocaleString
          });
        } else {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.maxRangeInclusive.msg', {
            maxValue: maxValueLocaleString
          });
        }
      } else if (isEqual(parsedValues.strRawValue, parsedMaxValue) && incMode < 2) {
        isError = true;
        status = 'alert';
        message = localize('rangeValidator.maxRangeExclusive.msg', {
          maxValue: maxValueLocaleString
        });
      } else if (isLess(parsedValues.strRawValue, parsedMinValue)) {
        if (incMode === 0 || incMode === 2) {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.minRangeExclusive.msg', {
            minValue: minValueLocaleString
          });
        } else {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.minRangeInclusive.msg', {
            minValue: minValueLocaleString
          });
        }
      } else if (isEqual(parsedValues.strRawValue, parsedMinValue) && !(incMode === 1 || incMode === 3)) {
        isError = true;
        status = 'alert';
        message = localize('rangeValidator.minRangeExclusive.msg', {
          minValue: minValueLocaleString
        });
      }
    }
  }
  return {
    isError,
    status,
    message,
    values: parsedValues
  };
};
//# sourceMappingURL=util.js.map